import React, { useEffect, useRef } from "react";
import {
  filterBy,
  CompositeFilterDescriptor,
} from "@progress/kendo-data-query";
import {
  Grid as KendoGrid,
  GridColumn as Column,
  GridCellProps,
  GridFilterChangeEvent,
  GridPageChangeEvent,
  GridCustomHeaderCellProps,
  GridCustomCellProps,
} from "@progress/kendo-react-grid";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { PagerTargetEvent } from "@progress/kendo-react-data-tools";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { Typography } from "@progress/kendo-react-common";
import { Button } from "@progress/kendo-react-buttons";
import { FiEdit } from "react-icons/fi";
import { MdDelete, MdKeyboardBackspace } from "react-icons/md";
import { FaHotel, FaRoute } from "react-icons/fa6";
import { useNavigate } from "react-router-dom";
import { checkAcessRights } from "../../../_helper/helper";
import {
  DELETE_ACCESS,
  EDIT_ACCESS,
  INSERT_ACCESS,
} from "../../../_contstants/common";
import {
  closeDialog,
  openDialog,
} from "../../../components/dialog/dialogSlice";
import {
  clearTourBookingID,
  setTourBookingID,
} from "../../tourbooking/tourBookingSlice";
import IconButton from "../../../components/common/IconButton";
import { LoadingPanel } from "../../../components/layout/Loading";
import AlertBox from "../../../components/common/AlertBox";
import { deleteTourBooking } from "../../tourbooking/services/tourBooking.services";
import AppDialog, {
  DialogCloseButton,
} from "../../../components/dialog/Dialog";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { getDashboardBookings } from "../services/dashboard.services";

const DashboardBookings: React.FC = () => {
  const dispatch = useAppDispatch();
  const dateFilter = useAppSelector((state) => state.dashboard.dateFilter);
  const navigate = useNavigate();

  useEffect(() => {
    const payload = {
      FromDate: dateFilter?.FromDate ? dateFilter?.FromDate : "",
      ToDate: dateFilter?.ToDate ? dateFilter?.ToDate : "",
    };
    dispatch(getDashboardBookings(payload));
  }, [dateFilter]);

  return (
    <>
      <DeleteTourBookingDialog />
      <GridLayout cols={[{ width: "100%" }]}>
        <GridLayoutItem
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "4px 20px 20px 0",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <IconButton
              fillMode="solid"
              title="Back"
              onClick={() => navigate(-1)}
              icon={MdKeyboardBackspace}
              iconClassName="absolute-position"
              style={{ height: 30 }}
              iconStyle={{ fontSize: "20px" }}
            />
            <Typography.h4 style={{ marginBottom: 0, marginLeft: "3px" }}>
              Tour Booking List
            </Typography.h4>
          </div>
        </GridLayoutItem>
        <GridLayoutItem>
          <TourBookingGridComponent />
        </GridLayoutItem>
      </GridLayout>
    </>
  );
};

interface PageState {
  skip: number;
  take: number;
}

const initialDataState: PageState = { skip: 0, take: 10 };

const TourBookingGridComponent: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isEditAccess = checkAcessRights("/tourbooking", EDIT_ACCESS);
  const isDeleteAccess = checkAcessRights("/tourbooking", DELETE_ACCESS);
  const isHotelAddAccess = checkAcessRights("/hotelvoucher", INSERT_ACCESS);
  const isRouteAddAccess = checkAcessRights("/routevoucher", INSERT_ACCESS);

  const TourBookingList = useAppSelector(
    (state) => state.dashboard.DashboardBookingList
  );
  const loading = useAppSelector((state) => state.dashboard.loading);

  const [filter, setFilter] = React.useState<
    CompositeFilterDescriptor | undefined
  >(undefined);
  const [page, setPage] = React.useState<PageState>(initialDataState);
  const [pageSizeValue, setPageSizeValue] = React.useState<
    number | string | undefined
  >();

  const pageChange = (event: GridPageChangeEvent) => {
    const targetEvent = event.targetEvent as PagerTargetEvent;
    const take =
      targetEvent.value === "All" ? TourBookingList.length : event.page.take;

    if (targetEvent.value) {
      setPageSizeValue(targetEvent.value);
    }
    setPage({
      ...event.page,
      take,
    });
  };

  const MyEditCommandCell = (props: GridCellProps) => {
    const handleEdit = (item: any) => {
      navigate("/tourbooking/edit", {
        state: {
          TourBookingID: item?.ID,
        },
      });
    };
    const handleOpenDeleteDialog = (ID: number) => {
      dispatch(openDialog("deleteTourBooking"));
      dispatch(setTourBookingID(ID));
    };

    return (
      <td
        className={`action-td ${props.className}`}
        style={{ ...props.style, right: 0 }}
      >
        {isEditAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Edit"
            onClick={() => handleEdit(props.dataItem)}
            icon={FiEdit}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "20px" }}
          />
        )}
        {isDeleteAccess && (
          <IconButton
            type="button"
            fillMode="flat"
            size="small"
            title="Delete"
            onClick={() => handleOpenDeleteDialog(props.dataItem.ID)}
            icon={MdDelete}
            iconClassName="absolute-position"
            iconStyle={{ fontSize: "24px" }}
          />
        )}
      </td>
    );
  };

  const handleFilterChange = (e: GridFilterChangeEvent) => {
    setFilter(e.filter);
    setPage({ skip: 0, take: page.take });
  };
  const HeaderCustomCell = (props: GridCustomHeaderCellProps) => (
    <HeaderThElement
      columnId={props.thProps?.columnId || ""}
      {...props.thProps}
      className={`table-header ${
        props.index === 11 || props.index === 12 ? "locked-header" : ""
      }`}
    >
      {props.children}
    </HeaderThElement>
  );

  const CustomCell = (props: any) => {
    return (
      <td {...props.tdProps} colSpan={1} className={"table-cell"}>
        {props.children}
      </td>
    );
  };

  const MyDataCustomCell = (props: GridCustomCellProps) => (
    <CustomCell {...props} />
  );

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {TourBookingList && TourBookingList.length ? (
        <KendoGrid
          style={{ maxHeight: "calc(100vh - 150px)" }}
          className="responsive-table"
          filterable={true}
          filter={filter}
          resizable={true}
          reorderable={true}
          data={
            filter
              ? filterBy(TourBookingList, filter).slice(
                  page.skip,
                  page.take + page.skip
                )
              : TourBookingList.slice(page.skip, page.take + page.skip)
          }
          onFilterChange={handleFilterChange}
          skip={page.skip}
          take={page.take}
          total={TourBookingList.length}
          pageable={{
            buttonCount: 5,
            pageSizes: [5, 10, 15, "All"],
            pageSizeValue: pageSizeValue,
            type: "input",
          }}
          onPageChange={pageChange}
          cells={{
            headerCell: HeaderCustomCell,
            data: MyDataCustomCell,
          }}
        >
          <Column
            field="TourBookingNo"
            title="Booking No"
            width={150}
            cell={(props: any) => (
              <td>
                <p
                  className="hoverableName"
                  style={{ fontWeight: 600, cursor: "pointer", margin: 0 }}
                  onClick={() =>
                    window.open(
                      `/tourbookingdetails/${props.dataItem?.TourBookingNo}`
                    )
                  }
                >
                  {props.dataItem?.TourBookingNo}
                </p>
              </td>
            )}
          />
          <Column field="Name" title="Tourist Name" width={200} />
          <Column field="MobileNo" title="Contact No" width={150} />
          <Column field="Sector" title="Sector" width={150} />
          <Column field="TravelType" title="Travel Type" width={150} />
          <Column field="Tour" title="Tour" width={350} />
          <Column field="TourDateCode" title="Tour Code" width={150} />
          <Column field="NoOfNights" title="Nights" width={150} />
          <Column field="IsCancelled" title="Status" width={150} />
          <Column field="BookBy" title="Book By" width={200} />
          <Column field="BranchName" title="Branch" width={150} />
          {(isHotelAddAccess || isRouteAddAccess) && (
            <Column
              field="ID"
              title="Vouchers"
              width={"110px"}
              filterable={false}
              locked={true}
              cell={(props) => {
                return (
                  <td
                    style={{
                      display: "flex",
                      gap: 5,
                      justifyContent: "center",
                      ...props.style,
                      right: 110,
                    }}
                    className={props.className}
                  >
                    {isHotelAddAccess && (
                      <IconButton
                        type="button"
                        fillMode="flat"
                        size="small"
                        title="Hotel Voucher"
                        onClick={() =>
                          navigate("/hotelvoucher/edit", {
                            state: {
                              TourBookingID: props.dataItem.ID,
                            },
                          })
                        }
                        icon={FaHotel}
                        iconClassName="absolute-position"
                        iconStyle={{ fontSize: "20px" }}
                      />
                    )}
                    {isRouteAddAccess && (
                      <IconButton
                        type="button"
                        fillMode="flat"
                        size="small"
                        title="Route Voucher"
                        onClick={() =>
                          navigate("/routevoucher/edit", {
                            state: {
                              TourBookingID: props.dataItem.ID,
                            },
                          })
                        }
                        icon={FaRoute}
                        iconClassName="absolute-position"
                        iconStyle={{ fontSize: "20px" }}
                      />
                    )}
                  </td>
                );
              }}
            />
          )}
          {(isEditAccess || isDeleteAccess) && (
            <Column
              field="ID"
              title="Actions"
              cell={MyEditCommandCell}
              width={"110px"}
              filterable={false}
              locked={true}
            />
          )}
        </KendoGrid>
      ) : (
        <AlertBox />
      )}
    </>
  );
};

const DeleteTourBookingDialog: React.FC = () => {
  const dispatch = useAppDispatch();
  const TourBookingID = useAppSelector(
    (state) => state.tourBooking.TourBookingID
  );
  const dateFilter = useAppSelector((state) => state.dashboard.dateFilter);
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const handleDeleteAction = async (ID: number | null) => {
    if (ID) {
      const payload = {
        FromDate: dateFilter?.FromDate ? dateFilter?.FromDate : "",
        ToDate: dateFilter?.ToDate ? dateFilter?.ToDate : "",
      };

      const response = await dispatch(deleteTourBooking(ID));
      if (response?.meta?.requestStatus === "fulfilled") {
        dispatch(getDashboardBookings(payload));
        dispatch(closeDialog());
        dispatch(clearTourBookingID());
      } else {
        dispatch(getDashboardBookings(payload));
        dispatch(closeDialog());
        dispatch(clearTourBookingID());
      }
    }
  };

  return (
    <>
      {dialogName === "deleteTourBooking" && (
        <AppDialog>
          <>
            <Typography.h5>{"Delete Tour Booking"}</Typography.h5>
            <GridLayout>
              <GridLayoutItem>
                <Typography.p>
                  Are you sure you want to delete this Tour Booking?
                </Typography.p>
              </GridLayoutItem>
              <GridLayoutItem
                style={{
                  display: "flex",
                  justifyContent: "end",
                }}
              >
                <DialogCloseButton themeColor="error" />
                <Button
                  fillMode={"solid"}
                  themeColor={"error"}
                  onClick={() => handleDeleteAction(TourBookingID)}
                  type="button"
                >
                  Delete
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </>
        </AppDialog>
      )}
    </>
  );
};

export default DashboardBookings;
